import React from 'react';
import { motion } from 'motion/react';
import { Shield, CheckCircle2, Search, FileText, Zap, ArrowRight, ShieldAlert, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ClientIntakeForm from '@/src/components/ClientIntakeForm';

export default function AuditFunnel() {
  return (
    <div className="relative pt-32 pb-24 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,150,255,0.05),transparent_50%)]" />
        <div className="absolute top-0 left-0 w-full h-full cyber-grid opacity-10" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center mb-24 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 border border-accent/20 text-accent text-[10px] font-bold uppercase tracking-[0.4em]"
          >
            <ShieldAlert className="w-3 h-3" /> Critical Security Initiative
          </motion.div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-8xl font-display font-bold tracking-tighter leading-[0.8]"
          >
            Free Enterprise <br />
            <span className="text-accent glow-text">Security Audit</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-muted-foreground font-light max-w-2xl mx-auto leading-relaxed"
          >
            Get a comprehensive 360° view of your security posture. We identify vulnerabilities, compliance gaps, and risk factors before they become liabilities.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left Side: The Process */}
          <div className="lg:col-span-7 space-y-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass-card p-8 rounded-3xl border-white/5 space-y-6">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                  <Search className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-display font-bold">Deep Discovery</h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-light">
                  We analyze your external attack surface and internal network architecture to find hidden entry points.
                </p>
              </div>
              <div className="glass-card p-8 rounded-3xl border-white/5 space-y-6">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                  <Shield className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-display font-bold">Risk Assessment</h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-light">
                  Our experts evaluate your current security controls against industry benchmarks and threat intelligence.
                </p>
              </div>
              <div className="glass-card p-8 rounded-3xl border-white/5 space-y-6">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                  <FileText className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-display font-bold">Strategic Report</h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-light">
                  Receive a detailed executive summary and technical roadmap for immediate remediation.
                </p>
              </div>
              <div className="glass-card p-8 rounded-3xl border-white/5 space-y-6">
                <div className="w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center text-accent">
                  <BarChart3 className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-display font-bold">ROI Analysis</h3>
                <p className="text-muted-foreground text-sm leading-relaxed font-light">
                  We show you exactly how much a breach would cost and how our solutions protect your bottom line.
                </p>
              </div>
            </div>

            <div className="p-12 rounded-[3rem] bg-accent/5 border border-accent/20 space-y-8">
              <h3 className="text-3xl font-display font-bold tracking-tight">Why we offer this for free?</h3>
              <p className="text-muted-foreground leading-relaxed font-light">
                We believe every business deserves to know their risk. By providing this audit, we demonstrate our expertise and build trust with organizations that value high-end security intelligence. No strings attached.
              </p>
              <div className="flex items-center gap-4 text-accent font-bold">
                <CheckCircle2 className="w-6 h-6" />
                <span>100% Confidential</span>
                <CheckCircle2 className="w-6 h-6 ml-4" />
                <span>No Obligation</span>
              </div>
            </div>
          </div>

          {/* Right Side: Form */}
          <div className="lg:col-span-5 sticky top-32">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="glass-card p-10 rounded-[3rem] border-white/10 shadow-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 blur-[60px] rounded-full" />
              <div className="text-center mb-10 space-y-2 relative z-10">
                <h2 className="text-3xl font-display font-bold">Start Your <span className="text-accent">Audit</span></h2>
                <p className="text-sm text-muted-foreground font-light">Complete the intelligence profile below.</p>
              </div>
              <ClientIntakeForm />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
